import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from './utils.js';
const PopoverContext = React.createContext({
    open: false,
    setOpen: () => { },
    triggerRef: { current: null },
});
const Popover = ({ children, open: controlledOpen, onOpenChange }) => {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled ? onOpenChange : setInternalOpen;
    const triggerRef = React.useRef(null);
    return (_jsx(PopoverContext.Provider, { value: { open, setOpen, triggerRef }, children: _jsx("div", { className: "relative inline-block", children: children }) }));
};
const PopoverTrigger = ({ asChild = false, children }) => {
    const { open, setOpen, triggerRef } = React.useContext(PopoverContext);
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
    };
    const setRef = React.useCallback((el) => {
        triggerRef.current = el;
        // Merge with child ref when asChild
        if (asChild && React.isValidElement(children)) {
            const childRef = 'ref' in children ? children.ref : undefined;
            if (childRef != null) {
                if (typeof childRef === 'function')
                    childRef(el);
                else if (typeof childRef === 'object' && childRef && 'current' in childRef)
                    childRef.current = el;
            }
        }
    }, [asChild, children, triggerRef]);
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children, {
            ref: setRef,
            onClick: handleClick,
        });
    }
    return (_jsx("button", { ref: setRef, onClick: handleClick, className: "inline-block", children: children }));
};
function getPortalPosition(trigger, side, align) {
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const contentWidth = 256; // w-64
    let top = rect.top;
    let left = rect.left;
    switch (side) {
        case 'top':
            top = rect.top - gap;
            break;
        case 'right':
            left = rect.right + gap;
            break;
        case 'left':
            left = rect.left - contentWidth - gap;
            break;
        default:
            top = rect.bottom + gap;
    }
    if (side === 'bottom' || side === 'top') {
        switch (align) {
            case 'start':
                left = rect.left;
                break;
            case 'end':
                left = rect.right - contentWidth;
                break;
            default:
                left = rect.left + rect.width / 2 - contentWidth / 2;
        }
    }
    else if (side === 'right' || side === 'left') {
        switch (align) {
            case 'start':
                top = rect.top;
                break;
            case 'end':
                top = rect.bottom - 200;
                break;
            default:
                top = rect.top + rect.height / 2 - 100;
        }
    }
    return { top, left };
}
const PopoverContent = ({ className, side = 'bottom', align = 'start', portal = false, children, }) => {
    const { open, setOpen, triggerRef } = React.useContext(PopoverContext);
    const contentRef = React.useRef(null);
    const [portalPosition, setPortalPosition] = React.useState(null);
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (triggerRef.current?.contains(target))
                return;
            if (contentRef.current && !contentRef.current.contains(target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [open, setOpen, portal, triggerRef]);
    // Close popover on scroll when using portal — prevents "floating" popover disconnected from trigger
    React.useEffect(() => {
        if (!open || !portal)
            return;
        const handleScroll = () => setOpen(false);
        document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
        return () => document.removeEventListener('scroll', handleScroll, { capture: true });
    }, [open, portal, setOpen]);
    React.useLayoutEffect(() => {
        if (open && portal && triggerRef.current) {
            const pos = getPortalPosition(triggerRef.current, side, align);
            setPortalPosition(pos);
        }
        else {
            setPortalPosition(null);
        }
    }, [open, portal, side, align, triggerRef]);
    if (!open)
        return null;
    const content = (_jsx("div", { ref: contentRef, className: cn('min-w-72 max-w-96 rounded-md border bg-white dark:bg-gray-800 p-4 text-sm shadow-lg', 'animate-in fade-in-0 zoom-in-95', !portal && 'absolute z-[9999]', portal && 'fixed z-[99999]', !portal && side === 'bottom' && 'top-full mt-1', !portal && side === 'top' && 'bottom-full mb-1', !portal && side === 'right' && 'left-full ml-1', !portal && side === 'left' && 'right-full mr-1', !portal && align === 'start' && 'left-0', !portal && align === 'end' && 'right-0', !portal && align === 'center' && 'left-1/2 transform -translate-x-1/2', className), style: {
            maxHeight: '400px',
            overflowY: 'auto',
            ...(portal && portalPosition && { top: portalPosition.top, left: portalPosition.left }),
        }, children: children }));
    if (portal) {
        return createPortal(content, document.body);
    }
    return content;
};
export { Popover, PopoverTrigger, PopoverContent };
